import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Smartphone, Inbox, Users, KeyRound, CreditCard, Settings,
  FlaskConical, Building2, LogOut, Check, ChevronDown, FolderKanban, Webhook, Menu, X, LifeBuoy, ShieldCheck, DollarSign, Network, Code2, Megaphone, Tags, Ban, Activity, FileText,
  Plug, Handshake, BookOpen,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { isLocale, type Locale } from '@bzapper/i18n';
import { HelpButton } from '@/components/HelpButton';
import { ProjectSwitcher, useProjects } from '@/components/ProjectSwitcher';
import { Onboarding } from '@/components/Onboarding';
import { SupportWidget } from '@/components/SupportWidget';
import { useQuery } from '@tanstack/react-query';
import { UsageBanner } from '@/components/UsageBanner';
import { FreeQuotaBanner } from '@/components/FreeQuotaBanner';
import { DeletionBanner } from '@/components/DeletionBanner';
import { AdvisoryBanner } from '@/components/AdvisoryBanner';
import { AccountLocked } from '@/components/AccountLocked';
import { apiFetch } from '@/lib/api';
import { InitialsAvatar } from '@/components/InitialsAvatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics';
import { useMe, useSession } from '@/lib/auth';
import { hasManualLocale, useI18n } from '@/lib/i18n';
import { getProject } from '@/lib/api';
import { isOfficial } from '@/lib/projects';
import { useNewVersion } from '@/lib/version';
import { toast } from 'sonner';

type Item = {
  to: string; key: string; icon: LucideIcon;
  admin?: boolean; mobileOnly?: boolean;
  // officialOnly: existe só no trilho OFICIAL. Template aprovado é uma
  // construção da API da Meta — no trilho não oficial não há aprovação prévia,
  // e um menu que leva a "isto não existe aqui" é ruído.
  officialOnly?: boolean;
};

/** Nome simplificado: primeiro + último (ex.: "Vinicius Marconi ... Berni" → "Vinicius Berni"). */
function simplifyName(full?: string, fallback = ''): string {
  const parts = (full ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

// Funções no contexto do PROJETO ativo (logo abaixo do seletor).
const PROJECT_NAV: Item[] = [
  { to: '/', key: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/numbers', key: 'nav.numbers', icon: Smartphone },
  // No oficial, template é o que destrava falar com quem não escreveu: fica logo
  // depois do número, e não escondido no fim da lista.
  { to: '/templates', key: 'nav.templates', icon: FileText, officialOnly: true },
  { to: '/inbox', key: 'nav.inbox', icon: Inbox },
  { to: '/keys', key: 'nav.keys', icon: KeyRound },
  { to: '/webhooks', key: 'nav.webhooks', icon: Webhook },
  { to: '/campaigns', key: 'nav.campaigns', icon: Megaphone },
  { to: '/playground', key: 'nav.playground', icon: FlaskConical },
];

// Funções no contexto da CONTA (contatos e faturamento são da conta).
const ACCOUNT_NAV: Item[] = [
  { to: '/projects', key: 'nav.projects', icon: FolderKanban, admin: true },
  { to: '/widgets', key: 'nav.widgets', icon: Code2, admin: true },
  { to: '/contacts', key: 'nav.contacts', icon: Users },
  { to: '/contacts/taxonomies', key: 'nav.taxonomies', icon: Tags },
  { to: '/contacts/blocklist', key: 'nav.blocklist', icon: Ban },
  { to: '/account', key: 'nav.account', icon: Building2, admin: true },
  { to: '/billing', key: 'nav.billing', icon: CreditCard, admin: true },
  { to: '/connected-apps', key: 'nav.connectedApps', icon: Plug, admin: true },
  { to: '/settings', key: 'nav.settings', icon: Settings },
  // Suporte: no desktop existe o widget flutuante; no mobile vira item de menu.
  { to: '/support', key: 'nav.support', icon: LifeBuoy, mobileOnly: true },
  { to: '/ajuda', key: 'nav.ajuda', icon: BookOpen },
];

// Gestão da PLATAFORMA — só para o tenant dono (Berni Software).
const PLATFORM_NAV: Item[] = [
  { to: '/platform/health', key: 'nav.health', icon: Activity },
  { to: '/platform/finance', key: 'nav.finance', icon: DollarSign },
  { to: '/platform/plans', key: 'nav.adminPlans', icon: ShieldCheck },
  { to: '/platform/tenants', key: 'nav.tenants', icon: Network },
  { to: '/platform/stripe', key: 'nav.stripe', icon: KeyRound },
  { to: '/platform/partners', key: 'nav.partners', icon: Handshake },
];

const ALL_NAV: Item[] = [...PROJECT_NAV, ...ACCOUNT_NAV, ...PLATFORM_NAV];

const LANGS: { code: Locale; label: string; flag: string }[] = [
  { code: 'pt-BR', label: 'Português', flag: '🇧🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
];

export function AppShell() {
  const { logout } = useSession();
  const { data: me } = useMe(true);
  // Estado de exclusão da PRÓPRIA conta — se agendada, a conta fica bloqueada (tela cheia).
  const { data: delStatus } = useQuery({
    queryKey: ['account-deletion-status'],
    queryFn: async (): Promise<{ pending: boolean; run_after?: string }> => {
      const r = await apiFetch('GET', '/account/delete/status');
      return (r.ok ? r.data : { pending: false }) as { pending: boolean; run_after?: string };
    },
    refetchInterval: 30000,
  });
  const { locale, setLocale, t } = useI18n();
  const location = useLocation();
  // Versão nova publicada: avisa uma vez e recarrega na próxima troca de tela —
  // nunca no meio do que a pessoa está digitando.
  const newVersion = useNewVersion();
  const [versionPath] = useState(() => location.pathname);
  useEffect(() => {
    if (!newVersion) return;
    toast.info(t('version.newAvailable'), {
      duration: Infinity, id: 'bz-new-version',
      action: { label: t('version.reload'), onClick: () => window.location.reload() },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newVersion]);
  useEffect(() => {
    if (newVersion && location.pathname !== versionPath) window.location.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Fecha o menu mobile ao navegar (e bloqueia o scroll do body enquanto aberto).
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Sincroniza o idioma com o locale do usuário, se ele ainda não escolheu manualmente.
  useEffect(() => {
    if (!hasManualLocale() && me?.locale && isLocale(me.locale) && me.locale !== locale) {
      setLocale(me.locale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.locale]);

  const { data: projects = [] } = useProjects();
  const isAdmin = me?.role === 'admin' || me?.role === 'super_admin';
  const isPlatformAdmin = !!me?.is_platform_admin;
  const activeProject = projects.find((p) => p.id === getProject()) ?? projects.find((p) => p.is_default);
  const official = isOfficial(activeProject);
  const visible = (list: Item[]) =>
    list.filter((n) => (!n.admin || isAdmin) && (!n.officialOnly || official));
  const projectItems = visible(PROJECT_NAV);
  const accountItems = visible(ACCOUNT_NAV);
  // Casa a rota mais específica primeiro (ex.: /contacts/blocklist antes de /contacts).
  const current = [...ALL_NAV]
    .sort((a, b) => b.to.length - a.to.length)
    .find((n) => (n.to === '/' ? location.pathname === '/' : location.pathname.startsWith(n.to)));
  const currentLang = LANGS.find((l) => l.code === locale) ?? LANGS[0];

  // Destaque dirigido pelo item mais específico (`current`), NÃO pelo match por
  // prefixo do NavLink — senão /contacts acende junto com /contacts/blocklist etc.
  const linkClass = (to: string) =>
    cn(
      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
      current?.to === to
        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
        : 'text-muted-foreground hover:bg-sidebar-accent hover:text-foreground',
    );

  const doLogout = () => { logout(); navigate('/login'); };

  // Conteúdo de navegação reutilizado no sidebar (desktop) e no drawer (mobile).
  // `mobile` inclui itens mobileOnly (ex.: Suporte — no desktop há o widget flutuante).
  const navBody = (onNavigate?: () => void, mobile = false) => (
    <>
      {/* Lockup horizontal ocupando toda a largura (swap por tema). */}
      <div className="px-1 pb-4">
        <img src="/logo/svg/bzapper-logo-primary.svg" alt="bZapper" className="block h-auto w-full dark:hidden" />
        <img src="/logo/svg/bzapper-logo-reverse.svg" alt="bZapper" className="hidden h-auto w-full dark:block" />
      </div>

      {/* Seletor de projeto em destaque + funções de contexto de projeto. */}
      <ProjectSwitcher isAdmin={isAdmin} />
      <nav className="mt-2 flex flex-col gap-0.5">
        {projectItems.map((n) => (
          <NavLink key={n.to} to={n.to} end className={linkClass(n.to)} onClick={onNavigate}>
            <n.icon className="size-4" />
            {t(n.key)}
          </NavLink>
        ))}
      </nav>

      {/* Separação visual: contexto da CONTA. */}
      <div className="mt-4 mb-1 border-t border-sidebar-border pt-3">
        <p className="px-3 pb-1 text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground/70">{t('nav.sectionAccount')}</p>
      </div>
      <nav className="flex flex-col gap-0.5">
        {accountItems.filter((n) => mobile || !n.mobileOnly).map((n) => (
          <NavLink key={n.to} to={n.to} end className={linkClass(n.to)} onClick={onNavigate}>
            <n.icon className="size-4" />
            {t(n.key)}
          </NavLink>
        ))}
      </nav>

      {/* Gestão da PLATAFORMA — só para o tenant dono (Berni Software). */}
      {isPlatformAdmin && (
        <>
          <div className="mt-4 mb-1 border-t border-sidebar-border pt-3">
            <p className="px-3 pb-1 text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground/70">{t('nav.sectionPlatform')}</p>
          </div>
          <nav className="flex flex-col gap-0.5">
            {PLATFORM_NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end className={linkClass(n.to)} onClick={onNavigate}>
                <n.icon className="size-4" />
                {t(n.key)}
              </NavLink>
            ))}
          </nav>
        </>
      )}

      <div className="mt-auto pt-3">
        <button
          onClick={() => { onNavigate?.(); doLogout(); }}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground"
        >
          <LogOut className="size-4" /> {t('nav.logout')}
        </button>
      </div>
    </>
  );

  // Conta agendada para exclusão → bloqueia tudo, só a tela de cancelamento.
  if (delStatus?.pending) return <AccountLocked runAfter={delStatus.run_after} />;

  return (
    <>
    <div className="flex min-h-screen bg-background text-foreground">
      {/* Sidebar (desktop) */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col gap-1 overflow-y-auto border-r border-sidebar-border bg-sidebar p-4 md:flex">
        {navBody()}
      </aside>

      {/* Drawer (mobile) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-72 max-w-[85%] flex-col gap-1 overflow-y-auto border-r border-sidebar-border bg-sidebar p-4 shadow-xl">
            <button onClick={() => setMobileOpen(false)} className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent" aria-label="Fechar">
              <X className="size-5" />
            </button>
            {navBody(() => setMobileOpen(false), true)}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b bg-card/80 px-4 py-3 backdrop-blur md:px-6">
          <div className="flex min-w-0 items-center gap-2 md:gap-3">
            <button onClick={() => setMobileOpen(true)} className="-ml-1 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent md:hidden" aria-label="Menu">
              <Menu className="size-5" />
            </button>
            <h1 className="truncate text-lg font-semibold">{current ? t(current.key) : 'bZapper'}</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* "?" da tela aberta: vai direto à seção que a ensina (ver HelpButton). */}
            <HelpButton navKey={current?.key} titulo={current ? t(current.key) : undefined} />
            {/* Seletor de idioma (à esquerda) */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent" aria-label={t('topbar.language')}>
                <span className="text-base leading-none">{currentLang.flag}</span>
                <ChevronDown className="size-3 text-muted-foreground" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem]">
                {LANGS.map((l) => (
                  <DropdownMenuItem key={l.code} onClick={() => { track('language_changed', { locale: l.code }); setLocale(l.code); }} className="gap-2">
                    <span className="text-base">{l.flag}</span>
                    <span className="flex-1">{l.label}</span>
                    {l.code === locale && <Check className="size-4 text-primary" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Usuário: avatar + nome simplificado, com dropdown (à direita) */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2.5 text-sm outline-none transition-colors hover:bg-accent">
                <InitialsAvatar name={me?.name} email={me?.email} src={me?.avatar_url} size={28} />
                <span className="hidden font-medium sm:inline">{simplifyName(me?.name, me?.email ?? '')}</span>
                <ChevronDown className="size-3 text-muted-foreground" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem]">
                <DropdownMenuItem onClick={() => navigate('/settings')} className="gap-2">
                  <Settings className="size-4" /> {t('nav.settings')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} className="gap-2 text-destructive focus:text-destructive">
                  <LogOut className="size-4" /> {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {/* pb extra: a bolha do widget de chat (bottom-right) não pode tapar o conteúdo final */}
        <main className="w-full min-w-0 flex-1 overflow-x-clip p-4 pb-24 md:p-6 md:pb-28">
          {isAdmin && <UsageBanner />}
          {/* Franquia do Free: aviso em 80% e envio pausado em 100% (Pro/cortesia não veem). */}
          {isAdmin && <FreeQuotaBanner />}
          {isAdmin && <DeletionBanner />}
          {/* Ação necessária na integração: não é dispensável por sessão como os
              outros — só some quando a pessoa marca como lido. */}
          {isAdmin && <AdvisoryBanner />}
          <Outlet />
        </main>
      </div>
      </div>

      {/* Overlays FORA do layout flex (não afetam a largura do conteúdo). */}
      <Onboarding isAdmin={isAdmin} />
      <SupportWidget />
    </>
  );
}
